import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { TwyneMarketRaw, TwyneMarketsRaw } from './types'

/**
 * Synthesized per-market lender key, e.g. `TWYNE_1_79CF33E6…_4C9EDD58…`.
 *
 * Keyed on `(intermediateVault, targetAsset)`. The TARGET VAULT is deliberately
 * left out: it is redundant on every live market (implied by the asset on
 * Euler, always the Pool on Aave) and a third address would make the key
 * unwieldy. The metadata generator asserts no two whitelisted triples ever
 * collapse onto one key — that assertion is what makes this safe.
 */
export function twyneLenderKey(
  lender: string,
  chainId: string | number,
  intermediateVault: string,
  targetAsset: string,
): string {
  // NORMALISE THE PREFIX. Callers reach this with two different things in
  // `lender`: the public path passes the FAMILY key (`TWYNE`, the single
  // dispatch entry a chain-wide fetch enumerates), while the user-data parser
  // passes the PER-MARKET key it was queried with — and building
  // `${lender}_${chainId}_…` out of the latter produced
  // `TWYNE_1_<IV>_<ASSET>_1_<IV>_<ASSET>`, a key nothing else in the system
  // uses. Nothing failed: the meta lookup simply missed, and every Twyne
  // position silently rendered as "no position" while the row itself was fine.
  const family = String(lender).toUpperCase().split('_')[0]
  return `${family}_${chainId}_${intermediateVault.replace(/^0x/i, '').toUpperCase()}_${targetAsset
    .replace(/^0x/i, '')
    .toUpperCase()}`
}

/** Recover `{ lender, chainId, intermediateVault, targetAsset }` from a key. */
export function twyneKeyParts(
  key: string,
):
  | { lender: string; chainId: string; intermediateVault: string; targetAsset: string }
  | undefined {
  if (!key?.startsWith('TWYNE_')) return undefined
  const m = key.slice('TWYNE_'.length).match(/^(\d+)_([0-9A-Fa-f]{40})_([0-9A-Fa-f]{40})$/)
  if (!m) return undefined
  return {
    lender: 'TWYNE',
    chainId: m[1],
    intermediateVault: `0x${m[2].toLowerCase()}`,
    targetAsset: `0x${m[3].toLowerCase()}`,
  }
}

const SECONDS_PER_YEAR = 31_536_000
const BPS = 10_000
const RAY = 1e27

const toFloat = (v: bigint | undefined, dec: number) => (v === undefined ? 0 : Number(v) / 10 ** dec)

/** SPY (1e27) → nominal APR in percent — the package convention. */
function spyToApr(spy: bigint): number {
  return (Number(spy) / RAY) * SECONDS_PER_YEAR * 100
}

function currencyFor(address: string, decimals: number, symbol: string, tokens: GenericTokenList): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Build the public rows for every Twyne market.
 *
 * TWO ROWS PER MARKET, and they mean different things:
 *
 *  - the COLLATERAL leg (`underlyingAsset`) carries the LTV band and the
 *    delegation pool. Its `depositRate` is the EXTERNAL supply APR — what the
 *    receipt token earns underneath — because Twyne pays borrowers nothing for
 *    depositing;
 *  - the DEBT leg (`targetAsset`) carries the external borrow APR and the
 *    external liquidity. It is `collateralDisabled`, because a Twyne position
 *    can only ever post the market's one collateral asset.
 *
 * THREE deliberate absences, each of which would be a lie if defaulted:
 *
 *  1. **No borrower-side aggregate.** Collateral and debt live in per-position
 *     contracts that the factory only enumerates per borrower, so a
 *     protocol-wide total needs an event replay. `totalDebt` is therefore 0 and
 *     the descriptor says why — the alternative is inventing a number.
 *  2. **No single `collateralFactor`.** There is no per-market LTV, only a BAND
 *     `[externalLiqLtv x buffer, maxTwyneLTVs]` with a user-chosen value per
 *     position. The row publishes the FLOOR as the conservative threshold and
 *     the band in the descriptor; a consumer that renders one number should
 *     render the floor, never the ceiling.
 *  3. **A market whose external threshold could not be read is SKIPPED.**
 *     Without it there is no band at all, and a row with a defaulted LTV is
 *     worse than a missing row.
 */
export function convertTwyneMarketsToResponse(
  raw: TwyneMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = { intrinsicYields: {}, lenderRewards: {}, loaded: true },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  if (!raw?.config) return out

  for (const row of raw.markets ?? []) {
    const m = row.market
    if (row.externalLiqLtv === undefined || row.maxTwyneLiqLtv === undefined) continue

    const lenderKey = twyneLenderKey(raw.lender, chainId, m.intermediateVault, m.targetAsset)
    // @ts-ignore — built incrementally, matching the Curvance/Resupply converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    const buffer = row.externalLiqBuffer ?? BigInt(BPS)
    // The FLOOR of the band: a position may never choose an LTV below this.
    const floorLtv = Number(row.externalLiqLtv * buffer) / (BPS * BPS)
    const maxLtv = Number(row.maxTwyneLiqLtv) / BPS
    const extLtv = Number(row.externalLiqLtv) / BPS

    const collAddr = m.underlyingAsset.toLowerCase()
    const debtAddr = m.targetAsset.toLowerCase()
    const collToken = tokens[collAddr]
    const debtToken = tokens[debtAddr]
    const collPrice =
      prices[toOracleKey(collToken?.assetGroup!) || toGenericPriceKey(collAddr, chainId)] || 0
    const debtPrice =
      prices[toOracleKey(debtToken?.assetGroup!) || toGenericPriceKey(debtAddr, chainId)] || 0

    // The delegation pool, expressed in the collateral asset. `cash` is the
    // credit still reservable — THE constraint on how much extra LTV a new
    // position can buy, and the reason a market can read healthy while being
    // unable to open anything.
    const creditTotal = toFloat(row.creditTotalAssets, row.creditDecimals)
    const creditAvailable = toFloat(row.creditCash, row.creditDecimals)
    const creditUtilization = row.creditTotalAssets > 0n ? Number(row.creditTotalBorrows) / Number(row.creditTotalAssets) : 0
    const creditApr = spyToApr(row.creditRateSpy)

    const externalBorrowLiquidity = toFloat(row.externalBorrowLiquidity, row.targetDecimals)
    // A matured PT still services existing positions but must not take new ones:
    // its price converges and the external threshold ramps out from under it.
    const openHalted = row.paused || row.collateralMatured || creditAvailable <= 0

    // --- collateral leg ----------------------------------------------------
    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: m.symbol ?? collToken?.symbol ?? 'collateral',
      poolId: m.intermediateVault.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, row.collateralDecimals, m.symbol ?? '', tokens),
      totalDeposits: creditTotal,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: creditAvailable,
      borrowLiquidity: 0,
      totalLiquidityUSD: creditAvailable * collPrice,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: creditTotal * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: creditUtilization,
      // What the collateral earns is the EXTERNAL protocol's supply rate — the
      // receipt token keeps earning underneath. Twyne adds nothing here; its
      // own rate is a COST, charged on reserved credit in this same asset.
      //
      // A ZERO HERE IS USUALLY CORRECT, not a decode failure: every live
      // Aave-side collateral (the PTs, wstETH) is a collateral-only reserve with
      // borrowing disabled, so Aave's `currentLiquidityRate` really is ~0. The
      // yield those assets pay is INTRINSIC — a PT's accretion to par, wstETH's
      // staking rate — and the stack joins it in as `intrinsicYield`. Do not
      // "fix" this by substituting the intrinsic rate here; that would
      // double-count it.
      depositRate: (row.externalSupplyRate ?? 0) * 100,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      rateModel: 'twyne-curve',
      intrinsicYield: 0,
      rewards: undefined,
      decimals: row.collateralDecimals,
      config: {
        0: {
          category: 0,
          // The floor of the band, i.e. the LTV a position gets with no credit
          // reserved. Anything higher is a per-position choice — see
          // `openParameter`, which carries the DOMAIN while `modes[posId]`
          // carries the value.
          borrowCollateralFactor: floorLtv,
          collateralFactor: floorLtv,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
          /**
           * THE PARAMETER. Twyne has no per-market LTV: each position picks its
           * own `twyneLiqLTV` inside this band and may change it at any time.
           *
           * `identityMapping` is what makes a curve unnecessary — the chosen
           * value IS the liquidation threshold, so a consumer prices a position
           * straight from `modes[posId]`. Without it every position would be
           * priced at `default` (the FLOOR), i.e. as though it had bought no
           * extra LTV at all, which is the entire product missing.
           *
           * `immutableAfterOpen: false` and NO cooldown: unlike LlamaLend's band
           * count this is adjustable in place, and unlike Liquity's rate there
           * is no adjustment delay. The only friction is economic — raising it
           * reserves more credit and can fail when the credit vault is dry.
           */
          openParameter: {
            kind: 'twyne-liq-ltv',
            dimension: 'collateralFactor',
            domain: { min: floorLtv, max: maxLtv },
            default: floorLtv,
            immutableAfterOpen: false,
            identityMapping: true,
          },
        },
      },
      closeFactor: 1,
      collateralActive: !openHalted,
      borrowingEnabled: false,
      depositsEnabled: !openHalted,
      hasStable: false,
      variableBorrowDisabled: true,
      isActive: !row.paused,
      isFrozen: row.paused,
    } as any

    // --- debt leg ----------------------------------------------------------
    const debtUid = createMarketUid(chainId, lenderKey, debtAddr)
    entry.data[debtUid] = {
      marketUid: debtUid,
      name: debtToken?.symbol ?? 'debt',
      poolId: m.intermediateVault.toLowerCase(),
      underlying: debtAddr,
      asset: currencyFor(debtAddr, row.targetDecimals, debtToken?.symbol ?? '', tokens),
      // Borrower aggregates are NOT readable without an event replay — see the
      // header. Zero here means "not measured", and the descriptor says so.
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: externalBorrowLiquidity,
      borrowLiquidity: externalBorrowLiquidity,
      totalLiquidityUSD: externalBorrowLiquidity * debtPrice,
      borrowLiquidityUSD: externalBorrowLiquidity * debtPrice,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      // THE market's borrow rate: the external protocol's. The Twyne credit leg
      // is charged on reserved credit in the COLLATERAL asset, so it cannot be
      // added to this number — its effective contribution is per-position.
      variableBorrowRate: (row.externalBorrowRate ?? 0) * 100,
      stableBorrowRate: 0,
      rateModel: 'twyne-curve',
      intrinsicYield: 0,
      rewards: undefined,
      decimals: row.targetDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: openHalted,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !openHalted,
      depositsEnabled: false,
      hasStable: false,
      variableBorrowDisabled: openHalted,
      isActive: !row.paused,
      isFrozen: row.paused,
    } as any

    entry.params = {
      market: {
        lender: lenderKey,
        // COLLATERAL / DEBT, then the venue — a Twyne market is a PAIR, and the
        // collateral alone does not name one: the Euler eWETH credit vault backs
        // three markets (USDC, USDT, WBTC), which a collateral-only label
        // renders as three identical rows. The roster carries this string; the
        // fallback exists for a roster row published before the label did, and
        // must not silently degrade to the ambiguous form.
        name:
          m.name ??
          `${m.symbol ?? '?'} / ${debtToken?.symbol ?? '?'} · ${
            m.vaultType === 'AAVE_V3' ? 'Aave V3' : 'Euler V2'
          }`,
        loanDecimals: row.targetDecimals,
        collateralDecimals: row.collateralDecimals,
        id: m.intermediateVault.toLowerCase(),
        // The FLOOR, in the protocol's own 1e4 scale. Never the ceiling: a
        // consumer rendering one number must render the conservative one.
        lltv: String(Math.round(floorLtv * BPS)),
        oracle: raw.config.oracleRouter ?? zeroAddress,
        irm: row.creditIrm ?? zeroAddress,
        collateralAddress: collAddr,
        loanAddress: debtAddr,
        twyne: {
          vaultType: m.vaultType,
          intermediateVault: m.intermediateVault,
          targetVault: m.targetVault,
          targetAsset: debtAddr,
          /** The receipt token the collateral vault actually holds. */
          collateralAsset: m.collateralAsset,
          aToken: m.aToken,
          categoryId: m.categoryId,
          /**
           * THE LTV BAND. There is no per-market LTV: a position picks its own
           * `twyneLiqLTV` anywhere in `[floorLtv, maxLtv]` and can change it at
           * any time, and the EFFECTIVE threshold is clamped further at runtime
           * when the credit vault runs short. Consumers must treat this as a
           * domain, not a value — the value lives on the position.
           */
          floorLtv,
          maxLtv,
          /** The external protocol's own liquidation threshold, 1e4-scaled. */
          externalLiqLtv: extLtv,
          externalLiqBufferBps: Number(buffer),
          /**
           * What the borrower pays ON RESERVED CREDIT, in the COLLATERAL asset —
           * NOT on their debt. Its contribution to an effective borrow rate is
           * `creditApr * (reservedCreditValue / debtValue)`, which only exists
           * per position. Never add it to `variableBorrowRate`.
           */
          creditApr,
          creditRateSpy: row.creditRateSpy.toString(),
          creditUtilization,
          /** Credit still reservable. Zero ⇒ no new position can raise its LTV
           *  above the floor, however healthy the market otherwise looks. */
          creditAvailable,
          creditTotal,
          creditIrm: row.creditIrm,
          /** Cap headroom for Credit-LPs, in collateral-asset units. */
          creditDepositCapacity:
            row.creditDepositCapacity === undefined ? undefined : row.creditDepositCapacity.toString(),
          /** Unix seconds; absent when the collateral has no maturity. */
          collateralMaturity: m.collateralMaturity,
          /** Computed at READ time. Two of the three live PT markets are past
           *  it — a stored flag would be stale. */
          collateralMatured: row.collateralMatured,
          /** Factory-wide pause; blocks deposits, borrows and LTV changes. */
          paused: row.paused,
          /**
           * Borrower-side aggregates are absent, not zero. Positions are
           * per-borrower CONTRACTS and the factory only enumerates them per
           * borrower, so a protocol-wide total needs a `T_CollateralVaultCreated`
           * replay — too heavy for a public refresh. `totalDebt` on the rows
           * above is therefore 0 and means "not measured".
           */
          borrowerAggregatesAvailable: false,
        },
      },
    } as any

    out[lenderKey] = entry
  }
  return out
}
